import { AxiosError, AxiosResponse } from 'axios';
import { DefaultTFuncReturn } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useGetLoginUserprivilege } from '../../../../api/auth/auth';
import { ErrorInfo, Production, ProductionSearchResult } from '../../../../api/model';
import {
  usePostProductionOrieditable,
  usePostProductionOutputOrireplist,
} from '../../../../api/productions/productions';
import { usePostUtilsDownload } from '../../../../api/utils/utils';
import { useDefaultOptions, useOptionsForBinary } from '../../../../hooks/api';
import { executeDownload } from '../../../../utils/download';
import * as sessionStorageUtils from '../../../../utils/sessionStorage';
import ErrorComponent from '../../../atoms/ErrorComponent';
import NoDataComponent from '../../../atoms/NoDataComponent';
import WaitComponent from '../../../atoms/WaitComponent';
import BlobErrorModal from '../../../modules/BlobErrorModal';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import OkModal from '../../../modules/OkModal';
import PageNavi from '../../../modules/PageNavi';
import SpinnerModal from '../../../modules/SpinnerModal';

// 文字列を 12 文字目まで表示する
const shorten = (text: string | undefined) => ((text?.length ?? 0) > 12 ? `${text?.substring(0, 12) ?? ''}...` : text);

// 文字列を 20 文字目まで表示する
const shorten20 = (text: string | undefined) =>
  (text?.length ?? 0) > 20 ? `${text?.substring(0, 20) ?? ''}...` : text;

// 制作一覧のソートキー
type SortKey =
  | 'production_no'
  | 'sales_my'
  | 'model_name'
  | 'production_division_name'
  | 'door_name'
  | 'engine_name'
  | 'destination_name'
  | 'factory_name'
  | 'production_status_name'
  | 'maker_company_name'
  | 'development_scale_name'
  | 'supplimentary_infomation'
  | 'model_user_name'
  | 'secret_management_number'
  | 'language_name'
  | 'parent_production_no'
  | 'completed'
  | 'completion_rate'
  | 'pending_issues_count'
  | 'count_target'
  | 'proofreading_on'
  | 'mass_production_on'
  | 'remaining_days'
  | 'title';

interface SortableThProps {
  label: DefaultTFuncReturn;
  onClick: () => void;
}

/**
 * ソート可能なテーブルヘッダー
 * @param param0
 * @returns
 */
function SortableTh({ label, onClick }: SortableThProps) {
  return (
    <th>
      <button type="button" className="text-button" onClick={onClick}>
        {label}
        <i className="fa-solid fa-sort" />
      </button>
    </th>
  );
}

// 1ページに表示する件数
const N_DATA_PER_PAGE = 50;

interface Props {
  data: AxiosResponse<ProductionSearchResult, unknown> | undefined;
  isLoading: boolean;
  isError: boolean;
  error: AxiosError<ErrorInfo> | null;
}

/**
 * 制作一覧
 * @param param0
 * @returns
 */
function ProductionsList({ data, isLoading, isError, error }: Props) {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // 現在表示中のページ番号
  const [currentPage, setCurrentPage] = useState(sessionStorageUtils.get('Productions.CurrentPage', 1));

  // データの取得
  const options = useDefaultOptions(language);
  const optionsForBinary = useOptionsForBinary(language);

  // ログインユーザ情報取得
  const psLoginUserInfo = useGetLoginUserprivilege(options);
  const SysUser = useMemo(() => {
    if (psLoginUserInfo === undefined) return false;
    if (psLoginUserInfo.data?.data.privilege_id === undefined) return false;
    if (psLoginUserInfo.data?.data.privilege_id === null) return false;
    return psLoginUserInfo.data?.data.privilege_id.includes(1);
  }, [psLoginUserInfo]);

  // ソート情報
  const [sortInfo, setSortInfo] = useState(
    sessionStorageUtils.get('Productions.SortInfo', { key: 'sales_my' as SortKey, isDisc: false })
  );

  // 初回ロード中かどうか
  const refIsFirstLoading = useRef(true);

  // 一覧データをソートしたもの
  const sortedProductions = useMemo(
    () =>
      [...(data?.data.productions ?? [])].sort((a, b) => {
        const aAny = a as { [key: string]: string | undefined };
        const bAny = b as { [key: string]: string | undefined };
        const multiValue = sortInfo.isDisc ? -1 : 1;
        if (aAny[sortInfo.key] !== bAny[sortInfo.key]) {
          // 第一ソートキー
          return ((aAny[sortInfo.key] ?? '') > (bAny[sortInfo.key] ?? '') ? 1 : -1) * multiValue;
        }
        if (a.model_name !== b.model_name) {
          // 第二ソートキー
          return ((a.model_name ?? '') > (b.model_name ?? '') ? 1 : -1) * multiValue;
        }
        // 第三ソートキー
        return ((a.completed ?? '') > (b.completed ?? '') ? 1 : -1) * multiValue;
      }),
    [sortInfo, data]
  );

  // 現在のページに表示する一覧
  const productionsForPresentPage = useMemo(
    () => sortedProductions.slice(N_DATA_PER_PAGE * (currentPage - 1), N_DATA_PER_PAGE * currentPage),
    [currentPage, sortedProductions]
  );

  // 一覧のソートキーを変更する関数
  const changeSortKey = useCallback((key: SortKey) => {
    setSortInfo((pre) => {
      const next = { key, isDisc: pre.key === key && !pre.isDisc };
      sessionStorageUtils.set('Productions.SortInfo', next);
      return next;
    });
    setCurrentPage(1);
  }, []);

  // 現在ページを変更する関数
  const changeCurrentPage = useCallback(
    (next: number) => {
      sessionStorageUtils.set('Productions.CurrentPage', next);
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  // データが変更されたときの動作
  useEffect(() => {
    if (data?.data.productions != null) {
      if (refIsFirstLoading.current) {
        // 初回ロード完了時は初回ロードが完了したことを設定する
        refIsFirstLoading.current = false;
      } else {
        // 初回ロード以外にデータが変更されたときは、現在ページを 1 ページにする
        setCurrentPage(1);
      }
    }
  }, [data?.data.productions]);

  // 原稿編集画面に遷移
  const openOri = (x: Production) => {
    if (
      x.production_id !== undefined &&
      x.production_id !== null &&
      x.first_l2_section_id !== undefined &&
      x.first_l2_section_id !== null
    ) {
      const basePath = process.env.REACT_APP_ROUTER_BASEPATH || '';
      window.open(
        `${basePath}/${language}/productions/sections/${x.production_id.toString()}/ps?section_id=${x.first_l2_section_id.toString()}`,
        '_blank'
      );
    }
  };

  // 表示されている資料管理ID
  const productionIds = useMemo(() => {
    if (data === undefined) return [];
    if (data === null) return [];
    if (data.data.productions == null) return [];
    return data.data.productions.map(($item) => $item.production_id ?? 0);
  }, [data]);

  // #region チェックイン/チェックアウト
  const [confCheckin, setConfCheckin] = useState(false); // チェックイン確認
  const [confCheckout, setConfCheckout] = useState(false); // チェックアウト確認
  const [displayOkModal, setDisplayOkModal] = useState(0); // メッセージ

  // チェックイン/チェックアウト処理定義
  const [displayOrieditableError, setDisplayOrieditableError] = useState(false);
  const {
    mutateAsync: OrieditableAsync,
    isLoading: OrieditableIsLoading,
    error: OrieditableError,
  } = usePostProductionOrieditable(options);
  useEffect(() => {
    setDisplayOrieditableError(OrieditableError != null);
  }, [OrieditableError]);

  // ベース言語同期時の処理
  const onCheckInOut = useCallback(
    async (flg: boolean) => {
      await OrieditableAsync(
        { data: { check_flg: flg, production_ids: productionIds } },
        {
          onSuccess: () => {
            if (flg) {
              setDisplayOkModal(1);
            } else {
              setDisplayOkModal(2);
            }
          },
        }
      );
    },
    [OrieditableAsync, productionIds]
  );
  // #endregion

  // #region ORI一括置換L2リスト出力
  // 出力リスト作成API
  const [displayOriOutputError, setDisplayOriOutputError] = useState(false);
  const {
    mutateAsync: oriOutputAsync,
    isLoading: oriOutputIsLoading,
    error: oriOutputError,
  } = usePostProductionOutputOrireplist(options);
  useEffect(() => {
    setDisplayOriOutputError(oriOutputError != null);
  }, [oriOutputError]);

  // ダウンロードAPI
  const [displayDownLoadError, setDisplayDownLoadError] = useState(false); // ダウンロードエラー
  const [displayMessage, setDisplayMessage] = useState('');
  const { mutateAsync: downloadAsync, error: downloadError } = usePostUtilsDownload<AxiosError<Blob>>(optionsForBinary);
  useEffect(() => {
    setDisplayDownLoadError(downloadError != null);
  }, [downloadError]);

  // 出力リスト
  const onOriOutput = useCallback(async () => {
    const response = await oriOutputAsync({ data: { production_ids: productionIds } });
    if (response.status === 200) {
      // 処理が成功したら結果をダウンロード
      if (response.data.message !== '') {
        setDisplayMessage(response.data.message ?? '');
      } else if (response.data.result_file_info !== undefined && response.data.result_file_info !== null) {
        await downloadAsync(
          { data: response.data.result_file_info },
          {
            onSuccess: (responseDL) => {
              executeDownload(responseDL);
            },
          }
        );
      }
    }
  }, [oriOutputAsync, downloadAsync, setDisplayMessage, productionIds]);
  // #endregion

  // データ取得中
  if (isLoading) {
    return <WaitComponent />;
  }

  // エラー表示
  if (isError) {
    return <ErrorComponent error={error} />;
  }

  // データが空の場合
  if (data == null || data.data.productions == null) {
    return <NoDataComponent />;
  }

  return (
    <>
      <div className="wfr in-pgnavi documentlist">
        <div className="scroll">
          <table className="production-table">
            <tbody>
              {/* ヘッダー部 */}
              <tr>
                <SortableTh label={t('資料管理No')} onClick={() => changeSortKey('production_no')} />
                <SortableTh label={t('M/Y')} onClick={() => changeSortKey('sales_my')} />
                <SortableTh label={t('機種名')} onClick={() => changeSortKey('model_name')} />
                <SortableTh label={t('資料区分')} onClick={() => changeSortKey('production_division_name')} />
                <SortableTh label={t('ドア数')} onClick={() => changeSortKey('door_name')} />
                <SortableTh label={t('エンジン')} onClick={() => changeSortKey('engine_name')} />
                <SortableTh label={t('仕向')} onClick={() => changeSortKey('destination_name')} />
                <SortableTh label={t('工場')} onClick={() => changeSortKey('factory_name')} />
                <SortableTh label={t('ステータス')} onClick={() => changeSortKey('production_status_name')} />
                <SortableTh label={t('原作メーカー')} onClick={() => changeSortKey('maker_company_name')} />
                <SortableTh label={t('開発規模')} onClick={() => changeSortKey('development_scale_name')} />
                <SortableTh label={t('補足情報')} onClick={() => changeSortKey('supplimentary_infomation')} />
                <SortableTh label={t('機種担当')} onClick={() => changeSortKey('model_user_name')} />
                <SortableTh label={t('機密管理No')} onClick={() => changeSortKey('secret_management_number')} />
                <SortableTh label={t('言語')} onClick={() => changeSortKey('language_name')} />
                <SortableTh label={t('親資料No')} onClick={() => changeSortKey('parent_production_no')} />
                <SortableTh label={t('完了')} onClick={() => changeSortKey('completed')} />
                <SortableTh label={t('進捗率')} onClick={() => changeSortKey('completion_rate')} />
                <SortableTh label={t('懸案数')} onClick={() => changeSortKey('pending_issues_count')} />
                <SortableTh label={t('カウント対象')} onClick={() => changeSortKey('count_target')} />
                <SortableTh label={t('完成日')} onClick={() => changeSortKey('proofreading_on')} />
                <SortableTh label={t('量産日')} onClick={() => changeSortKey('mass_production_on')} />
                <SortableTh label={t('残日数')} onClick={() => changeSortKey('remaining_days')} />
                <SortableTh label={t('資料名')} onClick={() => changeSortKey('title')} />
              </tr>
              {/* データ本体 */}
              {productionsForPresentPage.map((x) => (
                <tr key={x.production_id}>
                  <td>
                    <Link
                      to={`/${language}/productions/edit/${x.production_id?.toString() ?? ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {' '}
                      {x.production_no}
                    </Link>
                  </td>
                  <td onClick={() => openOri(x)}>{shorten(x.sales_my)}</td>
                  <td onClick={() => openOri(x)}>{shorten(x.model_name)}</td>
                  <td onClick={() => openOri(x)}>{shorten20(x.production_division_name)}</td>
                  <td onClick={() => openOri(x)}>{shorten(x.door_name)}</td>
                  <td onClick={() => openOri(x)}>{shorten(x.engine_name)}</td>
                  <td onClick={() => openOri(x)}>{shorten(x.destination_name)}</td>
                  <td onClick={() => openOri(x)}>{shorten(x.factory_name)}</td>
                  <td onClick={() => openOri(x)}>{shorten(x.production_status_name)}</td>
                  <td onClick={() => openOri(x)}>{shorten(x.maker_company_name)}</td>
                  <td onClick={() => openOri(x)}>{shorten(x.development_scale_name)}</td>
                  <td onClick={() => openOri(x)}>{shorten(x.supplimentary_infomation)}</td>
                  <td onClick={() => openOri(x)}>{shorten(x.model_user_name)}</td>
                  <td onClick={() => openOri(x)}>{shorten(x.secret_management_number)}</td>
                  <td onClick={() => openOri(x)}>{shorten(x.language_name)}</td>
                  <td onClick={() => openOri(x)}>{shorten(x.parent_production_no)}</td>
                  <td onClick={() => openOri(x)}>{x.completed}</td>
                  <td onClick={() => openOri(x)}>{x.completion_rate}</td>
                  <td onClick={() => openOri(x)}>{x.pending_issues_count}</td>
                  <td onClick={() => openOri(x)}>{shorten(x.count_target)}</td>
                  <td onClick={() => openOri(x)}>{x.proofreading_on?.replaceAll('-', '/')}</td>
                  <td onClick={() => openOri(x)}>{x.mass_production_on?.replaceAll('-', '/')}</td>
                  <td onClick={() => openOri(x)}>{x.remaining_days}</td>
                  <td onClick={() => openOri(x)}>{shorten(x.title)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* ページング部分 */}
        <PageNavi
          current={currentPage}
          pageCount={Math.ceil(data.data.productions.length / (N_DATA_PER_PAGE * 1.0))}
          onChangePage={changeCurrentPage}
        />
      </div>
      {SysUser && productionIds.length > 0 && (
        <div className="master-navi mt20">
          <ul style={{ justifyContent: 'flex-end' }}>
            <li>
              <button type="button" className="text-button" onClick={() => onOriOutput()}>
                <i className="fa-solid fa-download" />
                {t('原稿一括置換対象L2出力')}
              </button>
            </li>
            <li>
              <button type="button" className="text-button" onClick={() => setConfCheckin(true)}>
                <i className="fa-solid fa-arrow-right-to-bracket" />
                {t('L2チェックイン')}
              </button>
            </li>
            <li>
              <button type="button" className="text-button" onClick={() => setConfCheckout(true)}>
                <i className="fa-solid fa-arrow-right-from-bracket" />
                {t('L2チェックアウト')}
              </button>
            </li>
          </ul>
        </div>
      )}
      {confCheckin && (
        <ConfirmModal
          onClickCancel={() => setConfCheckin(false)}
          onClickOk={() => {
            setConfCheckin(false);
            void onCheckInOut(true);
          }}
        >
          {t('全てのL2をチェックイン状態にしますか?')}
        </ConfirmModal>
      )}
      {confCheckout && (
        <ConfirmModal
          onClickCancel={() => setConfCheckout(false)}
          onClickOk={() => {
            setConfCheckout(false);
            void onCheckInOut(false);
          }}
        >
          {t('自身編集中の全L2をチェックアウトしますか?')}
        </ConfirmModal>
      )}

      {/* スピナーモーダルの表示 */}
      {OrieditableIsLoading && <SpinnerModal />}
      {/* チェックインアウトエラーの表示 */}
      {displayOrieditableError && (
        <ErrorModal error={OrieditableError} onClickOk={() => setDisplayOrieditableError(false)} />
      )}
      {displayOkModal > 0 && (
        <OkModal onClickOk={() => setDisplayOkModal(0)}>
          {displayOkModal === 1 && t('チェックイン処理が終了しました。')}
          {displayOkModal === 2 && t('チェックアウト処理が終了しました。')}
        </OkModal>
      )}

      {/* スピナーモーダルの表示 */}
      {oriOutputIsLoading && <SpinnerModal />}
      {/* 一括置換リスト作成時の例外エラー  */}
      {displayOriOutputError && <ErrorModal error={oriOutputError} onClickOk={() => setDisplayOriOutputError(false)} />}
      {/* 一括置換リストメッセージ */}
      {displayMessage !== '' && <OkModal onClickOk={() => setDisplayMessage('')}>{displayMessage}</OkModal>}
      {/* ダウンロード時のエラー  */}
      {displayDownLoadError && (
        <BlobErrorModal error={downloadError} onClickOk={() => setDisplayDownLoadError(false)} />
      )}
    </>
  );
}

export default ProductionsList;
